import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import {
  ArrowRight, Check, X, Terminal, MapPin, Plug, Shield,
  Clock, TrendingUp, Calendar, Zap, Crown, Building2, Globe, ArrowUp
} from 'lucide-react';
import {
  Brain, ChatCircleDots, ListChecks, UsersThree, ChartLineUp,
  CalendarBlank, NoteBlank, Robot, Plugs, DeviceMobile
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLANS = [
  {
    id: 'starter', name: 'Starter', monthlyINR: 699, icon: Zap,
    tagline: 'For small teams',
    features: [
      { name: 'Task Management', included: true },
      { name: 'Check-in / Check-out', included: true },
      { name: 'Calendar & iCal', included: true },
      { name: 'Nearby Task Scanner', included: true },
      { name: 'GPS Live Tracking', included: false },
      { name: 'Smart Task Prioritization', included: false },
      { name: 'AI Sales Assistant', included: false },
      { name: 'Customer Intelligence', included: false },
      { name: 'CRM Integrations', included: false },
    ],
  },
  {
    id: 'professional', name: 'Professional', monthlyINR: 1499, icon: Crown,
    tagline: 'For growing teams', popular: true,
    features: [
      { name: 'Task Management', included: true },
      { name: 'Check-in / Check-out', included: true },
      { name: 'Calendar & iCal', included: true },
      { name: 'Nearby Task Scanner', included: true },
      { name: 'GPS Live Tracking', included: true },
      { name: 'Smart Task Prioritization', included: true },
      { name: 'AI Sales Assistant', included: false },
      { name: 'Customer Intelligence', included: false },
      { name: 'CRM Integrations', included: false },
    ],
  },
  {
    id: 'enterprise', name: 'Enterprise', monthlyINR: 2499, icon: Building2,
    tagline: 'Full AI suite',
    features: [
      { name: 'Task Management', included: true },
      { name: 'Check-in / Check-out', included: true },
      { name: 'Calendar & iCal', included: true },
      { name: 'Nearby Task Scanner', included: true },
      { name: 'GPS Live Tracking', included: true },
      { name: 'Smart Task Prioritization', included: true },
      { name: 'AI Sales Assistant', included: true },
      { name: 'Customer Intelligence', included: true },
      { name: 'CRM Integrations', included: true },
    ],
  },
];

const AI_FEATURES = [
  { icon: ChatCircleDots, name: 'AI Sales Assistant', desc: '24/7 AI companion for task management queries, tips, and operational guidance.', gridClass: 'md:col-span-4 md:row-span-2', chat: true },
  { icon: ListChecks, name: 'Smart Task Prioritization', desc: 'Auto-ranks daily tasks by deadline, value, and revenue potential.', gridClass: 'md:col-span-4 md:row-span-1' },
  { icon: UsersThree, name: 'Customer Intelligence', desc: 'Deep analysis of customer behavior, visit history, and engagement patterns.', gridClass: 'md:col-span-4 md:row-span-1' },
  { icon: ChartLineUp, name: 'Performance Analytics', desc: 'Daily AI reports with productivity scores and improvement suggestions.', gridClass: 'md:col-span-4 md:row-span-1' },
  { icon: CalendarBlank, name: 'Smart Visit Planning', desc: 'AI creates your day schedule optimizing for geography and priority.', gridClass: 'md:col-span-4 md:row-span-1' },
  { icon: NoteBlank, name: 'Smart Notes', desc: 'Transform rough field notes into structured docs with action items.', gridClass: 'md:col-span-4 md:row-span-1' },
  { icon: Robot, name: 'AI Task Command Center', desc: 'Natural language chatbot. Say "Create task for Rahul" and it just works.', gridClass: 'md:col-span-4 md:row-span-1', terminal: true },
];

const PLATFORM_FEATURES = [
  { icon: MapPin, name: 'Live GPS Tracking', desc: 'Real-time team locations with route trails' },
  { icon: Calendar, name: 'Calendar + iCal', desc: 'Google Calendar-style views with export' },
  { icon: Plug, name: '6+ CRM Integrations', desc: 'Zoho, HubSpot, Salesforce, Odoo' },
  { icon: Globe, name: 'Universal Webhooks', desc: 'Real-time event triggers to any endpoint' },
  { icon: Shield, name: 'RBAC Access Control', desc: 'Admin, Manager, Salesperson roles' },
  { icon: Clock, name: 'Check-in / Check-out', desc: 'Geo-tagged attendance with photo proof' },
  { icon: TrendingUp, name: 'Analytics Dashboard', desc: 'Interactive Recharts with 5 chart types' },
  { icon: Shield, name: '360 Audit Logs', desc: 'Track every action across the platform' },
];

const STATS = [
  { value: '9', label: 'AI Features', suffix: '' },
  { value: '6', label: 'CRM Integrations', suffix: '+' },
  { value: '40', label: 'Productivity Boost', suffix: '%' },
  { value: '360', label: 'Audit Coverage', suffix: '\u00B0' },
];

const CRM_NAMES = ['ZOHO', 'HUBSPOT', 'SALESFORCE', 'ODOO', 'FRESHSALES', 'PIPEDRIVE'];

function AnimatedCounter({ value, suffix }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);
  const num = parseInt(value);

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 1500;
    const step = Math.ceil(num / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= num) { setCount(num); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [inView, num]);

  return <span ref={ref}>{count}{suffix}</span>;
}

function Section({ children, className = '', id }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.section
      ref={ref}
      id={id}
      className={className}
      initial={{ opacity: 0 }}
      animate={inView ? { opacity: 1 } : {}}
      transition={{ duration: 0.6 }}
    >
      {children}
    </motion.section>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [annual, setAnnual] = useState(false);
  const [currency, setCurrency] = useState('INR');
  const [exchangeRate, setExchangeRate] = useState(84.5);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/api/exchange/rate`)
      .then(r => r.json())
      .then(d => setExchangeRate(d.usd_to_inr))
      .catch(() => {});
  }, []);

  const getPrice = (monthlyINR) => {
    let price = annual ? monthlyINR * 10 : monthlyINR;
    if (currency === 'USD') price = Math.round((price / exchangeRate) * 100) / 100;
    return price;
  };

  const formatPrice = (price) => {
    if (currency === 'USD') return `$${price < 10 ? price.toFixed(2) : Math.round(price)}`;
    return `\u20B9${Math.round(price).toLocaleString('en-IN')}`;
  };

  return (
    <div className="bg-white text-[#0A0A0A] min-h-screen overflow-hidden" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600;700&family=Outfit:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* ===== NAVBAR ===== */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-xl border-b border-[#E5E7EB]/40" data-testid="landing-navbar">
        <div className="max-w-7xl mx-auto px-6 lg:px-16 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-sm bg-[#002FA7] flex items-center justify-center">
              <span className="text-white font-bold text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>N</span>
            </div>
            <span className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>AIVORA NEXUS</span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-gray-500">
            <a href="#features" className="hover:text-[#0A0A0A] transition-colors">Features</a>
            <a href="#ai" className="hover:text-[#0A0A0A] transition-colors">AI Suite</a>
            <a href="#pricing" className="hover:text-[#0A0A0A] transition-colors">Pricing</a>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/login')} className="text-sm text-gray-600" data-testid="nav-login-btn">Log in</Button>
            <Button size="sm" onClick={() => navigate('/register')} className="bg-[#002FA7] hover:bg-[#001d6e] text-white text-sm rounded-sm px-5" data-testid="nav-signup-btn">Start Free Trial</Button>
          </div>
        </div>
      </nav>

      {/* ===== HERO ===== */}
      <section ref={heroRef} className="relative pt-40 pb-32 px-6 lg:px-16 overflow-hidden" data-testid="hero-section">
        <motion.div className="absolute inset-0 opacity-[0.04]" style={{ y: heroY }}>
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(#0A0A0A 1px, transparent 1px), linear-gradient(90deg, #0A0A0A 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }} />
        </motion.div>

        <div className="max-w-7xl mx-auto relative grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left — Copy */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>AI-Powered Task Management</p>
              <h1
                className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-none"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Every Task.<br />
                Every Team.<br />
                <span className="text-[#002FA7]">Connected.</span>
              </h1>
            </motion.div>

            <motion.p
              className="mt-8 text-lg text-gray-500 max-w-xl leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Backed with AI engines. Live GPS tracking. CRM integrations. 360-degree audit trail. One platform that empowers your entire task management and field operations stack.
            </motion.p>

            <motion.div
              className="mt-10 flex flex-wrap gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <Button
                onClick={() => navigate('/register')}
                className="bg-[#002FA7] hover:bg-[#001d6e] text-white rounded-sm px-8 py-6 text-base gap-2"
                data-testid="hero-signup-btn"
              >
                Start Free Trial <ArrowRight className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                onClick={() => { document.getElementById('ai')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="rounded-sm px-8 py-6 text-base border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
                data-testid="hero-features-btn"
              >
                View AI Capabilities
              </Button>
            </motion.div>
          </div>

          {/* Right — Animated AI Dashboard Mockup */}
          <motion.div
            className="hidden lg:block relative"
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="relative w-full aspect-[4/3] bg-[#0d1117] rounded-2xl border border-gray-200/60 shadow-2xl shadow-black/10 overflow-hidden" data-testid="hero-animation">
              {/* Window chrome */}
              <div className="flex items-center gap-1.5 px-4 h-8 bg-[#161b22] border-b border-white/10">
                <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
                <span className="text-[10px] text-gray-500 ml-3 font-mono">AIVORA Nexus — Dashboard</span>
              </div>

              {/* Dashboard body */}
              <div className="p-4 space-y-3">
                {/* Top metric cards */}
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { label: 'Tasks', value: '127', color: '#002FA7', trend: '+12%' },
                    { label: 'Completed', value: '89', color: '#22c55e', trend: '+8%' },
                    { label: 'Active Team', value: '24', color: '#f59e0b', trend: '95%' },
                    { label: 'AI Actions', value: '342', color: '#8b5cf6', trend: '+45%' },
                  ].map((card, i) => (
                    <motion.div
                      key={card.label}
                      className="rounded-lg border border-white/10 p-2.5 bg-white/[0.03]"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.8 + i * 0.12 }}
                    >
                      <p className="text-[9px] text-gray-500 font-mono">{card.label}</p>
                      <div className="flex items-end justify-between mt-1">
                        <span className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>{card.value}</span>
                        <span className="text-[8px] font-mono" style={{ color: card.color }}>{card.trend}</span>
                      </div>
                      <motion.div className="mt-1.5 h-1 rounded-full bg-white/10 overflow-hidden">
                        <motion.div
                          className="h-full rounded-full"
                          style={{ backgroundColor: card.color }}
                          initial={{ width: '0%' }}
                          animate={{ width: `${60 + i * 10}%` }}
                          transition={{ duration: 1.2, delay: 1 + i * 0.15, ease: 'easeOut' }}
                        />
                      </motion.div>
                    </motion.div>
                  ))}
                </div>

                {/* Main area: task list + AI panel */}
                <div className="grid grid-cols-5 gap-2">
                  {/* Task list */}
                  <div className="col-span-3 rounded-lg border border-white/10 bg-white/[0.02] p-2.5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">Active Tasks</span>
                      <motion.div className="flex items-center gap-1" animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, repeat: Infinity }}>
                        <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                        <span className="text-[8px] text-green-400 font-mono">LIVE</span>
                      </motion.div>
                    </div>
                    {[
                      { title: 'Visit MCGM Office', assignee: 'Rahul S.', status: 'in_progress', priority: 'high' },
                      { title: 'Submit PMC Report', assignee: 'Priya K.', status: 'completed', priority: 'medium' },
                      { title: 'Client Follow-up', assignee: 'Amit D.', status: 'pending', priority: 'high' },
                      { title: 'Site Survey - Pune', assignee: 'Rahul S.', status: 'in_progress', priority: 'low' },
                    ].map((task, i) => (
                      <motion.div
                        key={task.title}
                        className="flex items-center gap-2 py-1.5 border-b border-white/5 last:border-0"
                        initial={{ opacity: 0, x: -15 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 1.2 + i * 0.15 }}
                      >
                        <motion.div
                          className={`w-2 h-2 rounded-full flex-shrink-0 ${task.status === 'completed' ? 'bg-green-400' : task.status === 'in_progress' ? 'bg-blue-400' : 'bg-yellow-400'}`}
                          animate={task.status === 'in_progress' ? { scale: [1, 1.3, 1] } : {}}
                          transition={{ duration: 1.5, repeat: Infinity }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className={`text-[10px] text-white/80 truncate ${task.status === 'completed' ? 'line-through opacity-50' : ''}`}>{task.title}</p>
                        </div>
                        <span className="text-[8px] text-gray-500 font-mono flex-shrink-0">{task.assignee}</span>
                        <div className={`w-1 h-3 rounded-full flex-shrink-0 ${task.priority === 'high' ? 'bg-red-400' : task.priority === 'medium' ? 'bg-yellow-400' : 'bg-gray-600'}`} />
                      </motion.div>
                    ))}
                  </div>

                  {/* AI Activity Panel */}
                  <div className="col-span-2 rounded-lg border border-white/10 bg-white/[0.02] p-2.5">
                    <div className="flex items-center gap-1.5 mb-2">
                      <motion.div
                        className="w-4 h-4 rounded border border-cyan-500/40 flex items-center justify-center"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
                      >
                        <div className="w-1.5 h-1.5 bg-cyan-400 rounded-sm" />
                      </motion.div>
                      <span className="text-[10px] font-mono text-cyan-400 uppercase tracking-wider">AI Engine</span>
                    </div>
                    {[
                      { action: 'Prioritized 5 tasks', time: '2m ago', color: '#8b5cf6' },
                      { action: 'Route optimized: 30min saved', time: '5m ago', color: '#22c55e' },
                      { action: 'Smart note generated', time: '8m ago', color: '#f59e0b' },
                      { action: 'Customer insight: MCGM', time: '12m ago', color: '#3b82f6' },
                    ].map((log, i) => (
                      <motion.div
                        key={log.action}
                        className="flex items-start gap-1.5 py-1.5 border-b border-white/5 last:border-0"
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 1.5 + i * 0.2 }}
                      >
                        <div className="w-1 h-1 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: log.color }} />
                        <div className="flex-1 min-w-0">
                          <p className="text-[9px] text-white/70 truncate">{log.action}</p>
                          <p className="text-[7px] text-gray-600 font-mono">{log.time}</p>
                        </div>
                      </motion.div>
                    ))}
                    {/* Processing indicator */}
                    <motion.div
                      className="mt-2 flex items-center gap-1.5 px-2 py-1.5 rounded bg-cyan-500/10 border border-cyan-500/20"
                      animate={{ opacity: [0.6, 1, 0.6] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <motion.div className="flex gap-0.5">
                        {[0, 1, 2].map(j => (
                          <motion.div
                            key={j}
                            className="w-1 h-1 rounded-full bg-cyan-400"
                            animate={{ opacity: [0.2, 1, 0.2] }}
                            transition={{ duration: 0.8, repeat: Infinity, delay: j * 0.2 }}
                          />
                        ))}
                      </motion.div>
                      <span className="text-[8px] text-cyan-400 font-mono">Processing commands...</span>
                    </motion.div>
                  </div>
                </div>

                {/* Bottom GPS strip */}
                <motion.div
                  className="rounded-lg border border-white/10 bg-white/[0.02] p-2.5"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 2 }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-green-400" />
                        <span className="text-[9px] text-gray-400 font-mono">Live Tracking</span>
                      </div>
                      <div className="flex -space-x-1">
                        {['R', 'P', 'A', 'S'].map((initial, i) => (
                          <motion.div
                            key={initial}
                            className="w-4 h-4 rounded-full bg-[#002FA7]/40 border border-[#002FA7]/60 flex items-center justify-center"
                            animate={{ y: [0, -2, 0] }}
                            transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }}
                          >
                            <span className="text-[7px] text-white font-bold">{initial}</span>
                          </motion.div>
                        ))}
                      </div>
                      <span className="text-[8px] text-green-400 font-mono">4 online</span>
                    </div>
                    <div className="flex items-center gap-3">
                      {/* Mini progress bars for team */}
                      {[65, 89, 42, 78].map((val, i) => (
                        <div key={i} className="flex items-center gap-1">
                          <motion.div className="w-10 h-1 rounded-full bg-white/10 overflow-hidden">
                            <motion.div
                              className="h-full rounded-full bg-[#002FA7]"
                              initial={{ width: '0%' }}
                              animate={{ width: `${val}%` }}
                              transition={{ duration: 1.5, delay: 2.2 + i * 0.1 }}
                            />
                          </motion.div>
                          <span className="text-[7px] text-gray-500 font-mono">{val}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Scan line */}
              <motion.div
                className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#002FA7]/20 to-transparent pointer-events-none"
                animate={{ top: ['8%', '95%', '8%'] }}
                transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
              />
            </div>

            {/* Glow effect behind dashboard */}
            <div className="absolute -inset-4 bg-gradient-to-br from-[#002FA7]/5 via-transparent to-cyan-500/5 rounded-3xl blur-xl -z-10" />
          </motion.div>
        </div>
      </section>

      {/* ===== STATS STRIP ===== */}
      <Section className="border-y border-[#E5E7EB]" id="stats">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4">
          {STATS.map((stat, i) => (
            <motion.div
              key={stat.label}
              className={`px-6 lg:px-12 py-8 ${i < 3 ? 'border-r border-[#E5E7EB]' : ''} ${i < 2 ? 'border-b md:border-b-0 border-[#E5E7EB]' : ''}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <p className="text-4xl sm:text-5xl font-black tracking-tighter" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <AnimatedCounter value={stat.value} suffix={stat.suffix} />
              </p>
              <p className="text-sm text-gray-500 mt-1">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* ===== AI FEATURES BENTO GRID ===== */}
      <Section className="py-24 px-6 lg:px-16" id="ai">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>The AI Suite</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                9 AI engines.<br />Zero compromises.
              </h2>
              <p className="mt-4 text-gray-500 max-w-lg">Every feature is AI-driven. No caches, no patches, no fallbacks. Real intelligence, every request.</p>
            </div>

            {/* Animated AI Neural Network */}
            <motion.div
              className="hidden lg:flex items-center justify-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="relative w-80 h-64">
                {/* Central hub */}
                <motion.div
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-xl bg-[#002FA7] flex items-center justify-center shadow-lg shadow-[#002FA7]/30 z-10"
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <span className="text-white text-[10px] font-bold font-mono text-center leading-tight">GPT-5.2<br/>ENGINE</span>
                </motion.div>

                {/* Orbiting nodes — each represents an AI feature */}
                {[
                  { label: 'Route', angle: 0, color: '#22c55e', radius: 100 },
                  { label: 'Priority', angle: 40, color: '#f59e0b', radius: 110 },
                  { label: 'Notes', angle: 80, color: '#8b5cf6', radius: 95 },
                  { label: 'Search', angle: 120, color: '#ef4444', radius: 105 },
                  { label: 'Intel', angle: 160, color: '#06b6d4', radius: 100 },
                  { label: 'CRM', angle: 200, color: '#ec4899', radius: 110 },
                  { label: 'Audit', angle: 240, color: '#f97316', radius: 95 },
                  { label: 'Reports', angle: 280, color: '#14b8a6', radius: 105 },
                  { label: 'Chat', angle: 320, color: '#6366f1', radius: 100 },
                ].map((node, i) => {
                  const rad = (node.angle * Math.PI) / 180;
                  const x = Math.cos(rad) * node.radius;
                  const y = Math.sin(rad) * node.radius;
                  return (
                    <motion.div key={node.label} className="absolute left-1/2 top-1/2 z-20" style={{ x: x - 18, y: y - 18 }}>
                      {/* Connection line */}
                      <svg className="absolute" style={{ left: 18, top: 18, width: 1, height: 1, overflow: 'visible' }}>
                        <motion.line
                          x1="0" y1="0" x2={-x} y2={-y}
                          stroke={node.color}
                          strokeWidth="1"
                          strokeDasharray="4 4"
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 0.3 }}
                          viewport={{ once: true }}
                          transition={{ delay: 0.5 + i * 0.1 }}
                        />
                      </svg>
                      <motion.div
                        className="w-9 h-9 rounded-lg border flex items-center justify-center bg-white shadow-sm"
                        style={{ borderColor: node.color + '60' }}
                        initial={{ scale: 0, opacity: 0 }}
                        whileInView={{ scale: 1, opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 + i * 0.08, type: 'spring', stiffness: 200 }}
                        whileHover={{ scale: 1.2 }}
                      >
                        <span className="text-[7px] font-bold font-mono" style={{ color: node.color }}>{node.label}</span>
                      </motion.div>
                    </motion.div>
                  );
                })}

                {/* Pulsing rings around center */}
                {[1, 2, 3].map(ring => (
                  <motion.div
                    key={ring}
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#002FA7]/10"
                    style={{ width: ring * 70, height: ring * 70 }}
                    animate={{ scale: [1, 1.05, 1], opacity: [0.3, 0.15, 0.3] }}
                    transition={{ duration: 4, repeat: Infinity, delay: ring * 0.5 }}
                  />
                ))}

                {/* Data particles */}
                {[0, 1, 2, 3, 4].map(i => (
                  <motion.div
                    key={`p-${i}`}
                    className="absolute w-1 h-1 rounded-full bg-[#002FA7]"
                    style={{ left: '50%', top: '50%' }}
                    animate={{
                      x: [0, Math.cos(i * 72 * Math.PI / 180) * 120, 0],
                      y: [0, Math.sin(i * 72 * Math.PI / 180) * 120, 0],
                      opacity: [0, 0.8, 0],
                    }}
                    transition={{ duration: 3, repeat: Infinity, delay: i * 0.6 }}
                  />
                ))}
              </div>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5 mt-12">
            {AI_FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.name}
                  className={`${f.gridClass} group relative border border-[#E5E7EB] rounded-sm overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-[#0A0A0A] ${f.hero ? 'min-h-[320px]' : 'min-h-[180px]'}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  data-testid={`ai-feature-${f.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {/* Hero card with image */}
                  {f.hero && f.img && (
                    <div className="absolute inset-0 opacity-20 group-hover:opacity-30 transition-opacity">
                      <img src={f.img} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}

                  {/* Terminal card */}
                  {f.terminal && (
                    <div className="absolute bottom-4 right-4 opacity-10 group-hover:opacity-20 transition-opacity">
                      <Terminal className="w-24 h-24 text-[#002FA7]" />
                    </div>
                  )}

                  {/* Chat card */}
                  {f.chat && (
                    <div className="absolute top-0 right-0 w-full h-full opacity-[0.04] group-hover:opacity-[0.08] transition-opacity">
                      <div className="absolute top-16 right-6 w-48 space-y-2">
                        <div className="bg-[#002FA7] rounded-lg rounded-tr-none px-3 py-2 ml-auto w-36 h-4" />
                        <div className="bg-gray-300 rounded-lg rounded-tl-none px-3 py-2 w-44 h-4" />
                        <div className="bg-[#002FA7] rounded-lg rounded-tr-none px-3 py-2 ml-auto w-28 h-4" />
                      </div>
                    </div>
                  )}

                  <div className="relative z-10 p-6 lg:p-8 h-full flex flex-col justify-end">
                    <div className="w-10 h-10 rounded-sm bg-[#F3F4F6] flex items-center justify-center mb-4 group-hover:bg-[#002FA7] transition-colors">
                      <Icon size={22} weight="duotone" className="text-[#0A0A0A] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{f.name}</h3>
                    <p className="text-sm text-gray-500 mt-1 leading-relaxed">{f.desc}</p>
                  </div>

                  {/* Hover glow line */}
                  <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#002FA7] scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-500" />
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* ===== PLATFORM FEATURES ===== */}
      <Section className="py-24 px-6 lg:px-16 bg-[#F9FAFB]" id="features">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Platform Features</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Beyond AI. Everything else.
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 mt-12 border border-[#E5E7EB] rounded-sm overflow-hidden bg-white">
            {PLATFORM_FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.name}
                  className={`p-6 lg:p-8 ${i % 4 !== 3 ? 'lg:border-r' : ''} ${i < 4 ? 'border-b' : ''} border-[#E5E7EB] hover:bg-[#F9FAFB] transition-colors`}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Icon className="w-5 h-5 text-[#002FA7] mb-4" />
                  <h4 className="text-base font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>{f.name}</h4>
                  <p className="text-sm text-gray-500 mt-1">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* ===== CRM INTEGRATIONS MARQUEE ===== */}
      <Section className="py-20 px-6 lg:px-16 border-y border-[#E5E7EB] overflow-hidden" id="integrations">
        <div className="max-w-7xl mx-auto text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Integrations</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Connects to your CRM. Natively.
          </h2>
        </div>
        <div className="relative">
          <div className="flex gap-16 animate-marquee whitespace-nowrap">
            {[...CRM_NAMES, ...CRM_NAMES].map((name, i) => (
              <span
                key={i}
                className="text-6xl sm:text-7xl lg:text-8xl font-black tracking-tighter text-transparent select-none"
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  WebkitTextStroke: '1.5px #D1D5DB',
                }}
              >
                {name}
              </span>
            ))}
          </div>
        </div>
      </Section>

      {/* ===== PRICING ===== */}
      <Section className="py-24 px-6 lg:px-16" id="pricing">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Pricing</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Transparent. Simple.
              </h2>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm">
                <span className={currency === 'INR' ? 'font-semibold text-[#0A0A0A]' : 'text-gray-400'}>INR</span>
                <button
                  onClick={() => setCurrency(c => c === 'INR' ? 'USD' : 'INR')}
                  className={`relative w-12 h-6 rounded-full transition-colors ${currency === 'USD' ? 'bg-[#002FA7]' : 'bg-gray-300'}`}
                  data-testid="currency-toggle"
                >
                  <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${currency === 'USD' ? 'translate-x-[26px]' : 'translate-x-0.5'}`} />
                </button>
                <span className={currency === 'USD' ? 'font-semibold text-[#0A0A0A]' : 'text-gray-400'}>USD</span>
                <Globe className="w-3.5 h-3.5 text-gray-400 ml-1" />
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className={!annual ? 'font-semibold' : 'text-gray-400'}>Monthly</span>
                <Switch checked={annual} onCheckedChange={setAnnual} data-testid="billing-toggle" />
                <span className={annual ? 'font-semibold' : 'text-gray-400'}>Annual</span>
                {annual && <span className="text-xs font-medium text-[#002FA7] bg-[#002FA7]/10 px-2 py-0.5 rounded-sm">2 months free</span>}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#E5E7EB] rounded-sm overflow-hidden">
            {PLANS.map((plan, i) => {
              const Icon = plan.icon;
              return (
                <motion.div
                  key={plan.id}
                  className={`p-8 lg:p-10 ${i < 2 ? 'md:border-r border-[#E5E7EB]' : ''} ${plan.popular ? 'bg-[#0A0A0A] text-white' : ''} relative`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  data-testid={`pricing-${plan.id}`}
                >
                  {plan.popular && (
                    <div className="absolute top-4 right-4 text-xs font-bold uppercase tracking-[0.15em] bg-[#FFD700] text-[#0A0A0A] px-3 py-1 rounded-sm">
                      Popular
                    </div>
                  )}
                  <Icon className={`w-5 h-5 ${plan.popular ? 'text-[#FFD700]' : 'text-[#002FA7]'} mb-4`} />
                  <h3 className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{plan.name}</h3>
                  <p className={`text-sm mt-1 ${plan.popular ? 'text-gray-400' : 'text-gray-500'}`}>{plan.tagline}</p>

                  <div className="mt-6">
                    <span className="text-4xl font-black tracking-tighter" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {formatPrice(getPrice(plan.monthlyINR))}
                    </span>
                    <span className={`text-sm ml-1 ${plan.popular ? 'text-gray-400' : 'text-gray-500'}`}>
                      /user{annual ? '/year' : '/month'}
                    </span>
                  </div>

                  <p className={`mt-1 text-xs ${plan.popular ? 'text-gray-500' : 'text-gray-400'}`}>
                    Billed {annual ? 'annually' : 'monthly'} per user
                  </p>

                  <Button
                    onClick={() => navigate('/register')}
                    className={`w-full mt-6 rounded-sm ${
                      plan.popular
                        ? 'bg-white text-[#0A0A0A] hover:bg-gray-100'
                        : 'bg-[#002FA7] text-white hover:bg-[#001d6e]'
                    }`}
                    data-testid={`pricing-${plan.id}-cta`}
                  >
                    Get Started
                  </Button>

                  <div className="mt-6 space-y-2.5">
                    {plan.features.map((f) => (
                      <div key={f.name} className="flex items-center gap-2.5 text-sm">
                        {f.included ? (
                          <Check className={`w-4 h-4 flex-shrink-0 ${plan.popular ? 'text-[#FFD700]' : 'text-[#002FA7]'}`} />
                        ) : (
                          <X className={`w-4 h-4 flex-shrink-0 ${plan.popular ? 'text-gray-600' : 'text-gray-300'}`} />
                        )}
                        <span className={!f.included ? (plan.popular ? 'text-gray-600' : 'text-gray-400') : ''}>{f.name}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* ===== CTA ===== */}
      <Section className="py-32 px-6 lg:px-16 bg-[#002FA7] text-white text-center">
        <div className="max-w-2xl mx-auto">
          <motion.h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Ready to connect every task?
          </motion.h2>
          <motion.p
            className="mt-4 text-blue-200 text-lg"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            Join teams who replaced 5 tools with one AI platform.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <Button
              onClick={() => navigate('/register')}
              className="mt-8 bg-white text-[#002FA7] hover:bg-gray-100 rounded-sm px-10 py-6 text-base font-semibold"
              data-testid="cta-signup-btn"
            >
              Start Free Trial <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>
        </div>
      </Section>

      {/* ===== FOOTER ===== */}
      <footer className="relative bg-[#0A0A0A] text-white overflow-hidden" data-testid="landing-footer">
        {/* Animated background grid */}
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }} />

        {/* Floating particles */}
        {[...Array(12)].map((_, i) => (
          <motion.div
            key={`fp-${i}`}
            className="absolute w-1 h-1 rounded-full bg-[#002FA7]"
            style={{ left: `${8 + i * 8}%`, top: `${20 + (i % 4) * 18}%` }}
            animate={{
              y: [0, -30, 0],
              opacity: [0, 0.5, 0],
            }}
            transition={{ duration: 4 + i * 0.3, repeat: Infinity, delay: i * 0.5 }}
          />
        ))}

        {/* Brand text — single line */}
        <div className="relative max-w-7xl mx-auto px-6 lg:px-16 pt-24 pb-8">
          <div className="overflow-hidden whitespace-nowrap">
            {'AIVORA NEXUS'.split('').map((char, i) => (
              <motion.span
                key={`b-${i}`}
                className="inline-block text-[10vw] sm:text-[8vw] font-black tracking-tighter leading-none select-none"
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  background: 'linear-gradient(90deg, rgba(255,255,255,0.08) 0%, rgba(0,47,167,0.3) 50%, rgba(255,255,255,0.08) 100%)',
                  backgroundSize: '200% 100%',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  WebkitTextStroke: '1px rgba(255,255,255,0.12)',
                  marginRight: char === ' ' ? '0.15em' : undefined,
                }}
                initial={{ y: 80, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                {char === ' ' ? '\u00A0' : char}
              </motion.span>
            ))}
          </div>

          {/* Tagline under brand */}
          <motion.p
            className="mt-6 text-sm text-gray-600 max-w-md"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.7 }}
          >
            Every Task. Every Team. Connected. — AI-powered task management built for modern teams.
          </motion.p>
        </div>

        {/* Animated feature showcase */}
        <div className="relative max-w-7xl mx-auto px-6 lg:px-16 py-12">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {/* GPS Tracking animation */}
            <motion.div
              className="rounded-xl border border-white/10 bg-white/[0.03] p-5 overflow-hidden relative group"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-500 mb-4">Live Tracking</p>
              <div className="relative h-24">
                {/* Route path */}
                <svg viewBox="0 0 200 80" className="w-full h-full">
                  <motion.path
                    d="M 10 60 Q 50 10, 100 40 T 190 20"
                    fill="none"
                    stroke="rgba(0,47,167,0.4)"
                    strokeWidth="2"
                    strokeDasharray="6 4"
                    initial={{ pathLength: 0 }}
                    whileInView={{ pathLength: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 2, delay: 0.3 }}
                  />
                  {/* Waypoints */}
                  {[{ cx: 10, cy: 60 }, { cx: 100, cy: 40 }, { cx: 190, cy: 20 }].map((p, i) => (
                    <motion.circle
                      key={i}
                      cx={p.cx} cy={p.cy} r="4"
                      fill={i === 2 ? '#002FA7' : 'rgba(255,255,255,0.3)'}
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.5 + i * 0.4, type: 'spring' }}
                    />
                  ))}
                  {/* Moving dot */}
                  <motion.circle
                    r="3" fill="#22c55e"
                    animate={{ cx: [10, 100, 190], cy: [60, 40, 20] }}
                    transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
                  />
                </svg>
              </div>
              <div className="flex items-center gap-1.5 mt-2">
                <motion.div className="w-1.5 h-1.5 rounded-full bg-green-500" animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 1.5, repeat: Infinity }} />
                <span className="text-[10px] text-gray-500 font-mono">3 agents en route</span>
              </div>
            </motion.div>

            {/* AI Task Prioritization animation */}
            <motion.div
              className="rounded-xl border border-white/10 bg-white/[0.03] p-5 overflow-hidden relative"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-500 mb-4">AI Prioritization</p>
              <div className="space-y-2">
                {[
                  { label: 'Critical', w: '92%', color: '#ef4444', delay: 0.4 },
                  { label: 'High', w: '74%', color: '#f59e0b', delay: 0.6 },
                  { label: 'Medium', w: '51%', color: '#002FA7', delay: 0.8 },
                  { label: 'Low', w: '28%', color: '#6b7280', delay: 1.0 },
                ].map((bar) => (
                  <div key={bar.label}>
                    <div className="flex justify-between mb-0.5">
                      <span className="text-[9px] text-gray-400">{bar.label}</span>
                      <span className="text-[9px] text-gray-600 font-mono">{bar.w}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <motion.div
                        className="h-full rounded-full"
                        style={{ backgroundColor: bar.color }}
                        initial={{ width: '0%' }}
                        whileInView={{ width: bar.w }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: bar.delay, ease: 'easeOut' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <motion.div
                className="flex items-center gap-1 mt-3"
                animate={{ opacity: [0.3, 0.7, 0.3] }}
                transition={{ duration: 2.5, repeat: Infinity }}
              >
                <div className="w-3 h-3 rounded border border-purple-500/40 flex items-center justify-center">
                  <div className="w-1 h-1 bg-purple-400 rounded-sm" />
                </div>
                <span className="text-[9px] text-purple-400 font-mono">Auto-sorting 127 tasks</span>
              </motion.div>
            </motion.div>

            {/* Audit Log stream animation */}
            <motion.div
              className="rounded-xl border border-white/10 bg-white/[0.03] p-5 overflow-hidden relative"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-500 mb-4">360° Audit Trail</p>
              <div className="space-y-1.5 font-mono">
                {[
                  { action: 'task.created', user: 'Rahul', time: '0:02', color: '#22c55e' },
                  { action: 'route.optimized', user: 'AI', time: '0:05', color: '#002FA7' },
                  { action: 'checkin.logged', user: 'Priya', time: '0:12', color: '#f59e0b' },
                  { action: 'note.generated', user: 'AI', time: '0:18', color: '#8b5cf6' },
                  { action: 'status.updated', user: 'Amit', time: '0:24', color: '#06b6d4' },
                ].map((log, i) => (
                  <motion.div
                    key={log.action}
                    className="flex items-center gap-2 text-[9px]"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5 + i * 0.15 }}
                  >
                    <span className="text-gray-600">{log.time}</span>
                    <div className="w-1 h-1 rounded-full" style={{ backgroundColor: log.color }} />
                    <span className="text-gray-400">{log.action}</span>
                    <span className="text-gray-600 ml-auto">{log.user}</span>
                  </motion.div>
                ))}
              </div>
              <motion.div
                className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[#0A0A0A] to-transparent pointer-events-none"
              />
            </motion.div>

            {/* CRM Integration pulse animation */}
            <motion.div
              className="rounded-xl border border-white/10 bg-white/[0.03] p-5 overflow-hidden relative"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-500 mb-4">Integrations</p>
              <div className="relative h-28 flex items-center justify-center">
                {/* Center hub */}
                <div className="w-10 h-10 rounded-lg bg-[#002FA7]/20 border border-[#002FA7]/40 flex items-center justify-center z-10">
                  <span className="text-[8px] text-[#002FA7] font-bold font-mono">API</span>
                </div>
                {/* Connected nodes */}
                {[
                  { label: 'CRM', x: -50, y: -30, color: '#22c55e' },
                  { label: 'ERP', x: 50, y: -30, color: '#f59e0b' },
                  { label: 'Mail', x: -50, y: 30, color: '#ef4444' },
                  { label: 'Cal', x: 50, y: 30, color: '#8b5cf6' },
                ].map((node, i) => (
                  <motion.div key={node.label} className="absolute" style={{ left: `calc(50% + ${node.x}px - 14px)`, top: `calc(50% + ${node.y}px - 14px)` }}>
                    <motion.div
                      className="w-7 h-7 rounded-md border flex items-center justify-center bg-white/5"
                      style={{ borderColor: node.color + '50' }}
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.6 + i * 0.12, type: 'spring' }}
                    >
                      <span className="text-[7px] font-mono font-bold" style={{ color: node.color }}>{node.label}</span>
                    </motion.div>
                  </motion.div>
                ))}
                {/* Pulse rings */}
                {[1, 2].map(ring => (
                  <motion.div
                    key={ring}
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#002FA7]/10"
                    style={{ width: ring * 50, height: ring * 50 }}
                    animate={{ scale: [1, 1.15, 1], opacity: [0.2, 0.05, 0.2] }}
                    transition={{ duration: 3, repeat: Infinity, delay: ring * 0.8 }}
                  />
                ))}
              </div>
              <motion.p
                className="text-[9px] text-gray-500 font-mono text-center mt-1"
                animate={{ opacity: [0.3, 0.7, 0.3] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                Webhook sync active
              </motion.p>
            </motion.div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="relative max-w-7xl mx-auto px-6 lg:px-16 pb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-t border-white/10 pt-6">
            <div className="flex items-center gap-2.5">
              <div className="w-6 h-6 rounded-sm bg-[#002FA7] flex items-center justify-center">
                <span className="text-white font-bold text-[10px]" style={{ fontFamily: 'Outfit' }}>N</span>
              </div>
              <p className="text-xs text-gray-500">
                &copy; {new Date().getFullYear()} AIVORA Techlabs. All rights reserved.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <motion.div className="flex items-center gap-1.5" animate={{ opacity: [0.4, 0.8, 0.4] }} transition={{ duration: 3, repeat: Infinity }}>
                <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                <span className="text-[10px] text-gray-500 font-mono">All systems operational</span>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Accent glow at top edge */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#002FA7]/30 to-transparent" />
      </footer>

      {/* Marquee animation CSS */}
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 25s linear infinite;
        }
      `}</style>

      {/* Scroll to top */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.25 }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-8 right-8 z-50 w-11 h-11 rounded-full bg-[#002FA7] text-white shadow-lg shadow-[#002FA7]/30 flex items-center justify-center hover:bg-[#001d6e] transition-colors"
            data-testid="scroll-to-top-btn"
            aria-label="Scroll to top"
          >
            <ArrowUp className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
